import { useMemo, useCallback } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import { performGetRequest, performPutRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import type {
	AccessLevel,
	GetProjectAccessResponse,
	ProjectAccessLevel,
	ProjectAccessLevelResponse,
} from './types.tsx';

const getProjectAccessLevel = async (projectId?: number): Promise<ProjectAccessLevelResponse> => {
	if (!projectId) {
		throw new Error('Project ID is missing while fetching project access level.');
	}

	return performGetRequest(`/rest/internal/simplified/1.0/accesslevel/project/${projectId}`);
};

const getProjectAccessLevels = async (): Promise<ProjectAccessLevel[]> => {
	return performGetRequest('/rest/internal/simplified/1.0/accesslevel?projectType=software');
};

const updateProjectAccessLevel = async ({
	projectId,
	projectKey,
	accessLevel,
}: {
	projectId?: number;
	projectKey?: string;
	accessLevel: AccessLevel;
}) => {
	if (!projectId || !projectKey) {
		throw new Error('Project ID and/or Key is missing while updating project access level.');
	}

	return performPutRequest(`/rest/internal/simplified/1.0/projects/${projectId}/summary`, {
		body: JSON.stringify({
			accessLevel,
			key: projectKey,
		}),
	});
};

export const useProjectAccessLevel = () => {
	const { data: projectContext } = useProjectContext();

	const { projectId, projectKey } = useMemo(
		() => ({
			projectId: projectContext?.projectId,
			projectKey: projectContext?.projectKey,
		}),
		[projectContext],
	);

	const fetchProjectAccessLevels = useCallback(
		() =>
			Promise.all([getProjectAccessLevel(projectId), getProjectAccessLevels()]).then(
				([accessLevel, availableAccessLevels]) => ({
					accessLevel,
					availableAccessLevels,
				}),
			),
		[projectId],
	);

	const sendProjectAccessLevelUpdate = useCallback(
		({ accessLevel }: { accessLevel: AccessLevel }) =>
			updateProjectAccessLevel({ projectId, projectKey, accessLevel }),
		[projectId, projectKey],
	);

	const {
		loading: isLoading,
		error,
		data,
		fetch,
	} = useService<GetProjectAccessResponse>(fetchProjectAccessLevels);

	const {
		loading: isUpdating,
		error: updateError,
		fetch: update,
	} = useService<ProjectAccessLevelResponse, { accessLevel: AccessLevel }>(
		sendProjectAccessLevelUpdate,
	);

	return {
		loading: isLoading || isUpdating,
		error: error || updateError,
		data,
		fetch,
		update,
	};
};
